import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AutoChecklist,
  Car,
  CostLine,
  CreateCarInput,
  CreateMetingInput,
  CreateTariefInput,
  FleetCar,
  GeschatteKmStand,
  KoppelTijdlijnItem,
  Meting,
  OnderhoudsFactuur,
  RdwLookup,
  Tarief,
  UpdateCarInput,
} from '@fleet/shared';
import { api } from '../../lib/apiClient';

export type ArchiveReason = 'VERKOCHT' | 'TOTAL_LOSS' | 'INGELEVERD';

export const carKeys = {
  all: ['cars'] as const,
  active: ['cars', 'active'] as const,
  archived: ['cars', 'archived'] as const,
  detail: (k: string) => ['cars', 'detail', k] as const,
};

function invalidateFleet(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: carKeys.all });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
  qc.invalidateQueries({ queryKey: ['personen'] });
  qc.invalidateQueries({ queryKey: ['passen'] });
}

export function useActiveCars() {
  return useQuery({
    queryKey: carKeys.active,
    queryFn: () => api.get<{ cars: FleetCar[] }>('/cars?scope=active').then((r) => r.cars),
  });
}

export function useArchivedCars() {
  return useQuery({
    queryKey: carKeys.archived,
    queryFn: () => api.get<{ cars: FleetCar[] }>('/cars?scope=archived').then((r) => r.cars),
  });
}

export interface CarDetailData {
  car: Car;
  costLines: CostLine[];
  koppelTijdlijn: KoppelTijdlijnItem[];
  /** The car's dated fixed costs, oldest first per soort. */
  tarieven: Tarief[];
  /** Every odometer reading on this car, including the rejected ones. */
  metingen: Meting[];
  /** Current mileage, or null when no reading qualifies. */
  geschatteKmStand: GeschatteKmStand | null;
  /** The invoices behind the maintenance lines: link target and delete impact. */
  onderhoudsFacturen: OnderhoudsFactuur[];
  /** The new-car checklist, or null once it is done — see `CarDetail` on the worker. */
  checklist: AutoChecklist | null;
}

export function useCarDetail(kenteken: string) {
  return useQuery({
    queryKey: carKeys.detail(kenteken),
    queryFn: () => api.get<CarDetailData>(`/cars/${encodeURIComponent(kenteken)}/detail`),
  });
}

export function rdwLookup(kenteken: string): Promise<RdwLookup> {
  return api
    .get<{ lookup: RdwLookup }>(`/rdw/${encodeURIComponent(kenteken)}`)
    .then((r) => r.lookup);
}

export function useCreateCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCarInput) =>
      api.post<{ car: Car }>('/cars', input).then((r) => r.car),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useUpdateCar(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateCarInput) =>
      api.patch<{ car: Car }>(`/cars/${encodeURIComponent(kenteken)}`, patch).then((r) => r.car),
    onSuccess: () => {
      invalidateFleet(qc);
      qc.invalidateQueries({ queryKey: carKeys.detail(kenteken) });
    },
  });
}

export function useArchiveCar(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { reason: ArchiveReason; notitie?: string }) =>
      api.post<{ car: Car }>(`/cars/${encodeURIComponent(kenteken)}/archive`, v).then((r) => r.car),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useToggleGarage(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ car: Car }>(`/cars/${encodeURIComponent(kenteken)}/garage`).then((r) => r.car),
    onSuccess: () => {
      invalidateFleet(qc);
      qc.invalidateQueries({ queryKey: carKeys.detail(kenteken) });
    },
  });
}

/**
 * Readings and rates both hang off the car detail, and both feed the fleet
 * totals now, so every mutation invalidates the same set the car mutations do.
 */
function carPad(kenteken: string, rest: string): string {
  return `/cars/${encodeURIComponent(kenteken)}/${rest}`;
}

export function useAddMeting(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMetingInput) =>
      api.post<{ meting: Meting }>(carPad(kenteken, 'metingen'), input).then((r) => r.meting),
    onSuccess: () => {
      invalidateFleet(qc);
      qc.invalidateQueries({ queryKey: carKeys.detail(kenteken) });
    },
  });
}

export function useDeleteMeting(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(carPad(kenteken, `metingen/${id}`)),
    onSuccess: () => {
      invalidateFleet(qc);
      qc.invalidateQueries({ queryKey: carKeys.detail(kenteken) });
    },
  });
}

export function useAddTarief(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTariefInput) =>
      api.post<{ tarieven: Tarief[] }>(carPad(kenteken, 'tarieven'), input).then((r) => r.tarieven),
    onSuccess: () => {
      invalidateFleet(qc);
      qc.invalidateQueries({ queryKey: carKeys.detail(kenteken) });
    },
  });
}

export function useDeleteTarief(kenteken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(carPad(kenteken, `tarieven/${id}`)),
    onSuccess: () => {
      invalidateFleet(qc);
      qc.invalidateQueries({ queryKey: carKeys.detail(kenteken) });
    },
  });
}

/**
 * Tick or untick a step of the new-car checklist.
 *
 * The kenteken sits in the variables and not in the hook: the dashboard shows the
 * checklists of several cars at once and ticks them from one place. Invalidating
 * every dashboard period is deliberate — the page holds three of them (TCO, per
 * person, tankgedrag) and a stale one would show the step still open. The car's own
 * page carries the same checklist, so that goes too.
 */
export function useChecklistStap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kenteken,
      stap,
      gedaan,
    }: {
      kenteken: string;
      stap: string;
      gedaan: boolean;
    }) => {
      const url = carPad(kenteken, `checklist/${encodeURIComponent(stap)}`);
      return gedaan ? api.post(url) : api.del(url);
    },
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: carKeys.detail(v.kenteken) });
    },
  });
}
